import {
  Check,
  Clock3,
  KeyRound,
  LoaderCircle,
  RefreshCw,
  ShieldCheck,
  UserRound,
  X,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ConfirmationModal from "../../components/ConfirmationModal";
import {
  getPendingAccountAccessDecisionCount,
  isPendingAccountAccessDecision,
  isSelfAccountAccessGrant,
  useActorAccountAccessGrants,
} from "../../hooks/useActorAccountAccessGrants";
import accountAccessGrantsService, {
  type AccountAccessGrantTransitionInput,
  type AccountAccessGrantStatus,
  type AccountAccessGrantUserSummary,
  type ActorAccountAccessGrant,
} from "../../services/accountAccessGrantsService";
import type { HttpError } from "../../services/httpService";

type AccountAccessAction = "approve" | "reject" | "revoke";

interface PendingAccountAccessAction {
  action: AccountAccessAction;
  grantId: string;
  requestId: string;
  actorRelationship: "TARGET" | "GRANTEE";
  counterpart: AccountAccessGrantUserSummary;
  grant: ActorAccountAccessGrant;
}

export interface AlphaAccountAccessSectionProps {
  onGrantsLoaded?: (grants: ActorAccountAccessGrant[]) => void;
  /** Owns the complete revoke request when actor-context recovery must wrap the POST. */
  revokeGrant?: (
    grant: ActorAccountAccessGrant,
    input: AccountAccessGrantTransitionInput
  ) => Promise<void>;
  onAccountContextsChanged?: () => void | Promise<void>;
  onPersistentFeedback?: (
    tone: "success" | "error",
    message: string
  ) => void;
}

const STATUS_LABELS: Record<AccountAccessGrantStatus, string> = {
  PENDING_APPROVAL: "Pending approval",
  ACTIVE: "Active",
  REJECTED: "Rejected",
  CANCELLED: "Cancelled",
  REVOKED: "Revoked",
};

const STATUS_CLASSES: Record<AccountAccessGrantStatus, string> = {
  PENDING_APPROVAL: "border-v7-bronze/30 bg-v7-bronze/10 text-v7-bronze-dark",
  ACTIVE: "border-v7-success/30 bg-v7-success/10 text-v7-success",
  REJECTED: "border-v7-rust/30 bg-v7-rust/5 text-v7-rust",
  CANCELLED: "border-v7-ink/15 bg-v7-ink/[0.04] text-v7-ink-soft",
  REVOKED: "border-v7-ink/15 bg-v7-ink/[0.04] text-v7-ink-soft",
};

const EMPTY_GRANTS: ActorAccountAccessGrant[] = [];

function getHttpStatus(error: unknown): number | undefined {
  const httpError = error as HttpError;
  return httpError.status ?? httpError.response?.status;
}

function getListErrorMessage(error: unknown): string {
  const status = getHttpStatus(error);
  if (status === 401) {
    return "Your session is no longer authorized. Sign in again to review account access.";
  }
  if (status === 403) {
    return "Your signed-in account cannot review these access relationships.";
  }
  return "We could not load account access. Try again.";
}

function getActionErrorMessage(
  error: unknown,
  pendingAction: PendingAccountAccessAction
): string {
  const status = getHttpStatus(error);
  const actionLabel = pendingAction.action === "approve"
    ? "approve this request"
    : pendingAction.action === "reject"
      ? "reject this request"
      : pendingAction.actorRelationship === "TARGET"
        ? "revoke this access"
        : "relinquish this access";

  if (status === 400) {
    return `The request to ${actionLabel} is invalid. Close this dialog, refresh, and try again.`;
  }
  if (status === 401) {
    return `Your session is no longer authorized to ${actionLabel}. Sign in again.`;
  }
  if (status === 403) {
    return `Your signed-in account is not allowed to ${actionLabel}.`;
  }
  if (status === 404) {
    return "This access relationship is no longer available. Close this dialog and refresh.";
  }
  return `We could not ${actionLabel}. Try again.`;
}

function getCounterpart(grant: ActorAccountAccessGrant): AccountAccessGrantUserSummary {
  return grant.actorRelationship === "TARGET" ? grant.granteeUser : grant.targetUser;
}

function getIdentityName(user: AccountAccessGrantUserSummary): string {
  return user.name?.trim() || user.alias?.trim() || user.email;
}

function formatStatusDate(value: string): string | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getRelationshipDescription(grant: ActorAccountAccessGrant): string {
  if (grant.actorRelationship === "TARGET") {
    if (grant.status === "PENDING_APPROVAL") {
      return isPendingAccountAccessDecision(grant)
        ? "This person is requesting access to your account."
        : "This request is waiting for a lifecycle change.";
    }
    if (grant.status === "ACTIVE") {
      return "This person can access account-owned data for your account.";
    }
    return "This relationship no longer provides access to your account.";
  }

  if (grant.status === "PENDING_APPROVAL") {
    return "Your request is waiting for the account owner.";
  }
  if (grant.status === "ACTIVE") {
    return "You can use this account context while keeping your own session and roles.";
  }
  return "This relationship no longer lets you use the account context.";
}

const AccountIdentity: React.FC<{ user: AccountAccessGrantUserSummary }> = ({ user }) => {
  const displayName = getIdentityName(user);
  const alias = user.alias?.trim();
  return (
    <div className="min-w-0">
      <p className="truncate text-sm font-semibold text-v7-ink">{displayName}</p>
      {displayName !== user.email && (
        <p className="mt-0.5 truncate text-xs text-v7-ink/60">{user.email}</p>
      )}
      {alias && alias !== displayName && (
        <p className="mt-0.5 truncate text-xs text-v7-ink/60">Account: {alias}</p>
      )}
    </div>
  );
};

const AlphaAccountAccessSection: React.FC<AlphaAccountAccessSectionProps> = ({
  onGrantsLoaded,
  revokeGrant,
  onAccountContextsChanged,
  onPersistentFeedback,
}) => {
  const grantsQuery = useActorAccountAccessGrants();
  const grants = grantsQuery.data ?? EMPTY_GRANTS;
  const [feedback, setFeedback] = useState<{
    tone: "success" | "error";
    message: string;
  } | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAccountAccessAction | null>(
    null
  );
  const [reason, setReason] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  const [activeActionKey, setActiveActionKey] = useState<string | null>(null);
  const actionGuardRef = useRef(false);
  const publishFeedback = useCallback((
    tone: "success" | "error",
    message: string
  ): void => {
    setFeedback({ tone, message });
    onPersistentFeedback?.(tone, message);
  }, [onPersistentFeedback]);

  const visibleGrants = useMemo(
    () => grants.filter((grant) => !isSelfAccountAccessGrant(grant)),
    [grants]
  );

  useEffect(() => {
    if (grantsQuery.isSuccess) {
      onGrantsLoaded?.(visibleGrants);
    }
  }, [grantsQuery.isSuccess, onGrantsLoaded, visibleGrants]);

  const targetGrants = visibleGrants.filter(
    (grant) => grant.actorRelationship === "TARGET"
  );
  const granteeGrants = visibleGrants.filter(
    (grant) => grant.actorRelationship === "GRANTEE"
  );

  const openAction = (
    action: AccountAccessAction,
    grant: ActorAccountAccessGrant
  ): void => {
    if (grant.actorRelationship !== "TARGET" && grant.actorRelationship !== "GRANTEE") {
      return;
    }
    setPendingAction({
      action,
      grantId: grant.grantId,
      requestId: grant.currentRequestId,
      actorRelationship: grant.actorRelationship,
      counterpart: getCounterpart(grant),
      grant,
    });
    setReason("");
    setActionError(null);
  };

  const closeAction = (): void => {
    if (actionGuardRef.current) return;
    setPendingAction(null);
    setReason("");
    setActionError(null);
  };

  const confirmAction = async (): Promise<void> => {
    if (!pendingAction || actionGuardRef.current) return;
    const action = pendingAction;
    const actionKey = `${action.action}:${action.grantId}`;
    actionGuardRef.current = true;
    setActiveActionKey(actionKey);
    setActionError(null);
    setFeedback(null);

    try {
      const input = {
        requestId: action.requestId,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      };
      if (action.action === "approve") {
        await accountAccessGrantsService.approveGrant(action.grantId, input);
      } else if (action.action === "reject") {
        await accountAccessGrantsService.rejectGrant(action.grantId, input);
      } else if (revokeGrant) {
        await revokeGrant(action.grant, input);
      } else {
        await accountAccessGrantsService.revokeGrant(action.grantId, input);
      }

      setPendingAction(null);
      setReason("");
      let grantsRefreshFailed = false;
      try {
        await grantsQuery.refetch({ throwOnError: true });
      } catch {
        grantsRefreshFailed = true;
      }

      let accountRefreshFailed = false;
      if (action.action !== "reject" && onAccountContextsChanged) {
        try {
          await onAccountContextsChanged();
        } catch {
          accountRefreshFailed = true;
        }
      }

      if (grantsRefreshFailed && accountRefreshFailed) {
        publishFeedback(
          "error",
          "The access change succeeded, but current relationships and account choices could not reload. Refresh the page before taking another action or switching accounts."
        );
      } else if (grantsRefreshFailed) {
        publishFeedback(
          "error",
          "The access change succeeded, but current relationships could not reload. Refresh the page before taking another action."
        );
      } else if (accountRefreshFailed) {
        publishFeedback(
          "error",
          "The access change succeeded, but account choices could not refresh. Refresh the page before switching accounts."
        );
      } else {
        const message = action.action === "approve"
          ? "Access approved. Current relationships were refreshed."
          : action.action === "reject"
            ? "Access request rejected. Current relationships were refreshed."
            : action.actorRelationship === "TARGET"
              ? "Access revoked. Current relationships were refreshed."
              : "Access relinquished. Current relationships were refreshed.";
        publishFeedback("success", message);
      }
    } catch (error) {
      if (getHttpStatus(error) === 409) {
        setPendingAction(null);
        setReason("");
        let grantsRefreshFailed = false;
        try {
          await grantsQuery.refetch({ throwOnError: true });
        } catch {
          grantsRefreshFailed = true;
        }
        publishFeedback(
          "error",
          grantsRefreshFailed
            ? "This request changed before your decision. Current relationships could not reload. Refresh the page before taking another action. The action was not retried."
            : "This request changed before your decision. Current relationships were refreshed, and the action was not retried."
        );
      } else {
        const message = getActionErrorMessage(error, action);
        setActionError(message);
        onPersistentFeedback?.("error", message);
      }
    } finally {
      actionGuardRef.current = false;
      setActiveActionKey(null);
    }
  };

  const renderGrant = (grant: ActorAccountAccessGrant) => {
    const counterpart = getCounterpart(grant);
    const statusDate = formatStatusDate(grant.statusChangedAtUtc);
    const canDecide = isPendingAccountAccessDecision(grant);
    const canEndAccess = grant.status === "ACTIVE"
      && (grant.actorRelationship === "TARGET" || grant.actorRelationship === "GRANTEE");

    return (
      <article
        key={grant.grantId}
        aria-label={`Account access relationship with ${counterpart.email}`}
        className="rounded-v7-panel border border-v7-ink/15 bg-v7-white p-4"
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-v7-control bg-v7-paper text-v7-bronze-dark">
              {grant.actorRelationship === "TARGET" ? (
                <UserRound className="h-5 w-5" aria-hidden="true" />
              ) : (
                <KeyRound className="h-5 w-5" aria-hidden="true" />
              )}
            </div>
            <div className="min-w-0">
              <AccountIdentity user={counterpart} />
              <p className="mt-2 text-xs leading-5 text-v7-ink-soft">
                {getRelationshipDescription(grant)}
              </p>
              {statusDate && (
                <p className="mt-1 text-[11px] text-v7-ink/50">
                  Updated {statusDate}
                </p>
              )}
            </div>
          </div>

          <span className={`w-max shrink-0 rounded border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STATUS_CLASSES[grant.status]}`}>
            {STATUS_LABELS[grant.status]}
          </span>
        </div>

        {(canDecide || canEndAccess) && (
          <div className="mt-4 flex flex-wrap gap-2 border-t border-v7-ink/10 pt-4">
            {canDecide && (
              <>
                <button
                  type="button"
                  onClick={() => openAction("approve", grant)}
                  disabled={activeActionKey !== null}
                  className="inline-flex min-h-10 items-center gap-2 rounded-v7-control bg-v7-ink px-3 text-sm font-medium text-v7-white transition-colors hover:bg-v7-ink-soft disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Check className="h-4 w-4" aria-hidden="true" />
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => openAction("reject", grant)}
                  disabled={activeActionKey !== null}
                  className="inline-flex min-h-10 items-center gap-2 rounded-v7-control border border-v7-rust/40 bg-v7-white px-3 text-sm font-medium text-v7-rust transition-colors hover:bg-v7-rust/5 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                  Reject
                </button>
              </>
            )}
            {canEndAccess && (
              <button
                type="button"
                onClick={() => openAction("revoke", grant)}
                disabled={activeActionKey !== null}
                className="inline-flex min-h-10 items-center rounded-v7-control border border-v7-rust/40 bg-v7-white px-3 text-sm font-medium text-v7-rust transition-colors hover:bg-v7-rust/5 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {grant.actorRelationship === "TARGET"
                  ? "Revoke access"
                  : "Relinquish access"}
              </button>
            )}
          </div>
        )}
      </article>
    );
  };

  const renderRelationshipGroup = (
    id: string,
    title: string,
    description: string,
    items: ActorAccountAccessGrant[]
  ) => {
    if (items.length === 0) return null;
    return (
      <section aria-labelledby={id} className="space-y-3">
        <div>
          <h3 id={id} className="text-sm font-bold text-v7-ink">{title}</h3>
          <p className="mt-1 text-xs text-v7-ink-soft">{description}</p>
        </div>
        <div className="grid gap-3">{items.map(renderGrant)}</div>
      </section>
    );
  };

  const confirmationTitle = pendingAction?.action === "approve"
    ? "Approve account access"
    : pendingAction?.action === "reject"
      ? "Reject account access request"
      : pendingAction?.actorRelationship === "TARGET"
        ? "Revoke account access"
        : "Relinquish account access";
  const confirmationVerb = pendingAction?.action === "approve"
    ? "Approve access"
    : pendingAction?.action === "reject"
      ? "Reject request"
      : pendingAction?.actorRelationship === "TARGET"
        ? "Revoke access"
        : "Relinquish access";

  return (
    <section
      aria-labelledby="alpha-account-access-heading"
      className="ph-mask space-y-5"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 id="alpha-account-access-heading" className="text-base font-bold text-v7-ink">
            Account access
          </h2>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-v7-ink-soft">
            Review direct account relationships involving your signed-in user. Selecting another
            account never changes your session, roles, or security authority.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void grantsQuery.refetch()}
          disabled={grantsQuery.isFetching || activeActionKey !== null}
          className="inline-flex min-h-10 w-max shrink-0 items-center gap-2 rounded-v7-control border border-v7-ink/25 bg-v7-white px-3 text-sm font-medium text-v7-ink transition-colors hover:border-v7-ink disabled:cursor-not-allowed disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${grantsQuery.isFetching ? "animate-spin" : ""}`} aria-hidden="true" />
          Refresh
        </button>
      </div>

      {feedback && (
        <div
          role={feedback.tone === "error" ? "alert" : "status"}
          className={feedback.tone === "error"
            ? "rounded-v7-panel border border-v7-rust/30 bg-v7-rust/5 p-3 text-sm text-v7-rust"
            : "rounded-v7-panel border border-v7-success/30 bg-v7-success/10 p-3 text-sm text-v7-success"}
        >
          {feedback.message}
        </div>
      )}

      {grantsQuery.isLoading ? (
        <div role="status" className="flex min-h-40 items-center justify-center gap-3 rounded-v7-panel border border-v7-ink/15 bg-v7-white text-sm text-v7-ink-soft">
          <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
          Loading account access
        </div>
      ) : grantsQuery.isError ? (
        <div role="alert" className="rounded-v7-panel border border-v7-rust/30 bg-v7-rust/5 p-4 text-sm text-v7-rust">
          <p>{getListErrorMessage(grantsQuery.error)}</p>
          <button
            type="button"
            onClick={() => void grantsQuery.refetch()}
            className="mt-3 rounded-v7-control border border-v7-rust/40 bg-v7-white px-3 py-2 font-medium text-v7-rust transition-colors hover:bg-v7-rust/5"
          >
            Try again
          </button>
        </div>
      ) : visibleGrants.length === 0 ? (
        <div className="rounded-v7-panel border border-v7-ink/15 bg-v7-white py-14 text-center">
          <ShieldCheck className="mx-auto h-8 w-8 text-v7-ink/30" aria-hidden="true" />
          <p className="mt-3 text-sm font-medium text-v7-ink">No shared account access</p>
          <p className="mt-1 text-xs text-v7-ink-soft">
            Your permanent self access is active and is not shown here.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {getPendingAccountAccessDecisionCount(visibleGrants) > 0 && (
            <div className="flex items-start gap-3 rounded-v7-panel border border-v7-bronze/30 bg-v7-bronze/10 p-3 text-sm text-v7-ink">
              <Clock3 className="mt-0.5 h-4 w-4 shrink-0 text-v7-bronze-dark" aria-hidden="true" />
              <p>
                {getPendingAccountAccessDecisionCount(visibleGrants)} access {getPendingAccountAccessDecisionCount(visibleGrants) === 1 ? "request needs" : "requests need"} your decision.
              </p>
            </div>
          )}
          {renderRelationshipGroup(
            "alpha-account-access-target-heading",
            "Access to your account",
            "Requests and active relationships where you own the account.",
            targetGrants
          )}
          {renderRelationshipGroup(
            "alpha-account-access-grantee-heading",
            "Accounts shared with you",
            "Relationships where another person owns the account context.",
            granteeGrants
          )}
        </div>
      )}

      <ConfirmationModal
        isOpen={pendingAction !== null}
        onClose={closeAction}
        onConfirm={() => void confirmAction()}
        title={confirmationTitle}
        message={pendingAction ? (
          <div className="space-y-4">
            <p>
              {pendingAction.action === "approve"
                ? `Allow ${pendingAction.counterpart.email} to access account-owned data for your account? They keep their own signed-in session and roles.`
                : pendingAction.action === "reject"
                  ? `Reject the access request from ${pendingAction.counterpart.email}?`
                  : pendingAction.actorRelationship === "TARGET"
                    ? `Remove ${pendingAction.counterpart.email}'s access to your account?`
                    : `Stop using the account context owned by ${pendingAction.counterpart.email}?`}
            </p>
            <label className="block space-y-1">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-v7-ink/55">
                Optional reason
              </span>
              <textarea
                aria-label="Optional reason"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                maxLength={1000}
                rows={3}
                disabled={activeActionKey !== null}
                placeholder="Add a short note about your decision"
                className="w-full resize-y rounded-v7-control border border-v7-ink/20 bg-v7-white px-3 py-2 text-sm text-v7-ink outline-none transition-colors placeholder:text-v7-ink/40 focus:border-v7-bronze-dark focus:ring-1 focus:ring-v7-bronze-dark disabled:opacity-50"
              />
              <span className="block text-right text-[11px] text-v7-ink/50">
                {reason.length}/1000
              </span>
            </label>
            {actionError && (
              <p role="alert" className="rounded-v7-panel border border-v7-rust/30 bg-v7-rust/5 p-3 text-xs text-v7-rust">
                {actionError}
              </p>
            )}
          </div>
        ) : ""}
        confirmText={confirmationVerb}
        isDestructive={pendingAction?.action !== "approve"}
        loading={activeActionKey !== null}
        appearance="alpha"
      />
    </section>
  );
};

export default AlphaAccountAccessSection;
